import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';
import './AdminPage.css';

function Tab({ label, active, onClick }) {
  return <button className={`admin-tab ${active ? 'active' : ''}`} onClick={onClick}>{label}</button>;
}

function ScrapingTab() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['scraping-stats'],
    queryFn: () => api.get('/admin/scraping-stats').then(r => r.data),
    refetchInterval: 30000,
  });

  if (isLoading) return <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>;
  if (!stats) return null;

  const now = new Date();
  const nextScrape = new Date(now);
  nextScrape.setUTCHours(7, 0, 0, 0);
  if (nextScrape <= now) nextScrape.setDate(nextScrape.getDate() + 1);
  const msToScrape = nextScrape - now;
  const hToScrape = Math.floor(msToScrape / 3600000);
  const mToScrape = Math.floor((msToScrape % 3600000) / 60000);

  const currentHour = now.getUTCHours();
  const nextSyncHour = currentHour % 2 === 0 ? (now.getUTCMinutes() > 0 ? currentHour + 2 : currentHour) : currentHour + 1;
  const nextSync = new Date(now);
  nextSync.setUTCHours(nextSyncHour, 0, 0, 0);
  if (nextSync <= now) nextSync.setUTCHours(nextSync.getUTCHours() + 2);
  const msToSync = nextSync - now;
  const hToSync = Math.floor(msToSync / 3600000);
  const mToSync = Math.floor((msToSync % 3600000) / 60000);

  const cardStyle = { background: 'white', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: 20, marginBottom: 12 };
  const statRow = { display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13, borderBottom: '1px solid var(--color-border)' };
  const labelSt = { color: 'var(--color-text-muted)' };
  const valueSt = { fontWeight: 600 };

  return (
    <div className="admin-section" style={{ maxWidth: 600 }}>
      <h2>Scheduler</h2>

      <div style={cardStyle}>
        <h3 style={{ fontSize: 14, marginBottom: 12 }}>Next runs</h3>
        <div style={statRow}>
          <span style={labelSt}>Product scraping (ECO + SBA)</span>
          <span style={valueSt}>{hToScrape}h {mToScrape}m</span>
        </div>
        <div style={{ ...statRow, borderBottom: 'none' }}>
          <span style={labelSt}>Bid sync (Japanese prices)</span>
          <span style={valueSt}>{hToSync}h {mToSync}m</span>
        </div>
      </div>

      <div style={cardStyle}>
        <h3 style={{ fontSize: 14, marginBottom: 12 }}>Scraping queue</h3>
        {Object.entries(stats.queue).map(([source, statuses]) => (
          <div key={source} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 6 }}>{source}</div>
            {Object.entries(statuses).map(([status, count]) => (
              <div key={status} style={statRow}>
                <span style={labelSt}>{status}</span>
                <span style={{ ...valueSt, color: status === 'pending' ? 'var(--color-gold)' : status === 'failed' ? 'var(--color-error)' : 'inherit' }}>{count}</span>
              </div>
            ))}
          </div>
        ))}
        {Object.keys(stats.queue).length === 0 && <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Queue is empty.</p>}
      </div>

      <div style={cardStyle}>
        <h3 style={{ fontSize: 14, marginBottom: 12 }}>Products</h3>
        {Object.entries(stats.products).map(([source, count]) => (
          <div key={source} style={statRow}>
            <span style={labelSt}>{source}</span>
            <span style={valueSt}>{count}</span>
          </div>
        ))}
        <div style={{ ...statRow, borderBottom: 'none' }}>
          <span style={labelSt}>Total</span>
          <span style={valueSt}>{Object.values(stats.products).reduce((a, b) => a + b, 0)}</span>
        </div>
      </div>

      <div style={cardStyle}>
        <h3 style={{ fontSize: 14, marginBottom: 12 }}>Bid sync</h3>
        <div style={statRow}>
          <span style={labelSt}>Products with active bids</span>
          <span style={valueSt}>{stats.active_bids_products}</span>
        </div>
        <div style={{ ...statRow, borderBottom: 'none' }}>
          <span style={labelSt}>Total active bids</span>
          <span style={valueSt}>{stats.active_bids}</span>
        </div>
      </div>

      {stats.last_product_scraped && (
        <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 8, marginBottom: 16 }}>
          Last product scraped: {new Date(stats.last_product_scraped).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
        </div>
      )}

      {stats.logs && stats.logs.length > 0 && (
        <div style={cardStyle}>
          <h3 style={{ fontSize: 14, marginBottom: 12 }}>Recent logs</h3>
          <div style={{ maxHeight: 400, overflowY: 'auto', fontSize: 12, fontFamily: 'monospace' }}>
            {stats.logs.map((l, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, padding: '4px 0', borderBottom: '1px solid var(--color-border)', alignItems: 'baseline' }}>
                <span style={{ color: 'var(--color-text-muted)', whiteSpace: 'nowrap', minWidth: 110 }}>
                  {new Date(l.time).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
                <span style={{
                  padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap',
                  background: l.job === 'scraper_eco' ? '#E3F2FD' : l.job === 'scraper_sba' ? '#FFF3E0' : l.job === 'bid_sync' ? '#E8F5E9' : '#F3E5F5',
                  color: l.job === 'scraper_eco' ? '#1565C0' : l.job === 'scraper_sba' ? '#E65100' : l.job === 'bid_sync' ? '#2E7D32' : '#7B1FA2',
                }}>{l.job}</span>
                <span style={{ color: l.level === 'error' ? 'var(--color-error)' : 'var(--color-text)', wordBreak: 'break-word' }}>{l.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const [tab, setTab] = useState('requests');
  const qc = useQueryClient();
  const [openWishlistUserId, setOpenWishlistUserId] = useState(null);

  const { data: openUserWishlist = [], isLoading: loadingUserWishlist } = useQuery({
    queryKey: ['admin-user-wishlist', openWishlistUserId],
    queryFn: () => api.get(`/admin/users/${openWishlistUserId}/wishlist`).then(r => r.data),
    enabled: !!openWishlistUserId,
  });

  const { data: requests = [] } = useQuery({
    queryKey: ['access-requests'],
    queryFn: () => api.get('/admin/access-requests').then(r => r.data),
    enabled: tab === 'requests',
  });

  const { data: users = [] } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api.get('/admin/users').then(r => r.data),
    enabled: tab === 'users' || tab === 'notifications',
  });

  const { data: bids = [] } = useQuery({
    queryKey: ['admin-bids'],
    queryFn: () => api.get('/admin/bids').then(r => r.data),
    enabled: tab === 'bids',
  });

  const { data: validationQueue = [] } = useQuery({
    queryKey: ['model-validation'],
    queryFn: () => api.get('/admin/model-validation').then(r => r.data),
    enabled: tab === 'validation',
  });

  const approve = useMutation({
    mutationFn: (id) => api.post(`/admin/access-requests/${id}/approve`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['access-requests'] }),
  });

  const reject = useMutation({
    mutationFn: (id) => api.post(`/admin/access-requests/${id}/reject`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['access-requests'] }),
  });

  const [notifData, setNotifData] = useState({ titre: '', message: '', user_id: '' });
  const sendNotif = useMutation({
    mutationFn: () => api.post('/admin/notifications/send', {
      titre: notifData.titre, message: notifData.message,
      user_id: notifData.user_id || undefined
    }),
    onSuccess: () => { setNotifData({ titre: '', message: '', user_id: '' }); },
  });

  const [validationForm, setValidationForm] = useState({});
  const validate = useMutation({
    mutationFn: ({ id, data }) => api.post(`/admin/model-validation/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['model-validation'] }),
  });

  const labelStyle = { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 };

  return (
    <div className="admin-page">
      <h1>Admin</h1>
      <div className="admin-tabs">
        {[['requests', 'Access Requests'], ['users', 'Users'], ['bids', 'All Bids'], ['validation', 'Model Validation'], ['notifications', 'Notifications'], ['scraping', 'Scraping']].map(([key, label]) => (
          <Tab key={key} label={label} active={tab === key} onClick={() => setTab(key)} />
        ))}
      </div>

      {tab === 'requests' && (
        <div className="admin-section">
          <h2>Pending access requests ({requests.length})</h2>
          {requests.length === 0 && <p className="empty">No pending requests.</p>}
          {requests.map(r => (
            <div key={r.id} className="admin-row">
              <div>
                <div className="admin-name">{r.prenom} {r.nom}</div>
                <div className="admin-meta">{r.email} {r.societe && `\u00b7 ${r.societe}`}</div>
                <div className="admin-date">{new Date(r.created_at).toLocaleDateString('en-GB')}</div>
              </div>
              <div className="admin-actions">
                <button className="btn btn-primary btn-sm" onClick={() => approve.mutate(r.id)}>Approve</button>
                <button className="btn btn-outline btn-sm" onClick={() => reject.mutate(r.id)}>Reject</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'users' && (
        <div className="admin-section">
          <h2>All users ({users.length})</h2>
          <table className="admin-table">
            <thead><tr><th>Name</th><th>Email</th><th>Company</th><th>Status</th><th>Admin</th><th>Date</th><th>Wishlist</th></tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td>{u.prenom} {u.nom}</td>
                  <td>{u.email}</td>
                  <td>{u.societe || '-'}</td>
                  <td><span style={{ color: u.statut === 'approved' ? 'var(--color-success)' : u.statut === 'rejected' ? 'var(--color-error)' : 'var(--color-gold)' }}>{u.statut}</span></td>
                  <td>{u.is_admin ? '\u2713' : ''}</td>
                  <td>{new Date(u.created_at).toLocaleDateString('en-GB')}</td>
                  <td>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => setOpenWishlistUserId(openWishlistUserId === u.id ? null : u.id)}
                    >
                      {openWishlistUserId === u.id ? 'Hide' : 'View'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {openWishlistUserId && (
            <div className="user-wishlist-panel">
              <h3>
                Wishlist of {users.find(u => u.id === openWishlistUserId)?.prenom} {users.find(u => u.id === openWishlistUserId)?.nom}
              </h3>
              {loadingUserWishlist && <p className="empty">Loading…</p>}
              {!loadingUserWishlist && openUserWishlist.length === 0 && <p className="empty">This user has no wishlist items.</p>}
              <div className="user-wishlist-list">
                {openUserWishlist.map(item => {
                  let firstImg = null;
                  try {
                    const imgs = typeof item.images === 'string' ? JSON.parse(item.images) : item.images;
                    firstImg = imgs?.[0];
                  } catch {}
                  const startingPrice = item.price_ttc || Math.ceil((item.min_price_euro || 0) * 1.20);
                  const currentBid = item.best_bid ? Math.ceil(item.best_bid) : startingPrice;
                  return (
                    <div key={item.id} className="user-wishlist-row">
                      <div className="uw-image">
                        {firstImg ? <img src={firstImg} alt="" /> : <div className="uw-no-img">No image</div>}
                      </div>
                      <div className="uw-info">
                        <div className="uw-title">{item.clean_title}</div>
                        <div className="uw-brand">{item.brand}</div>
                        {item.recommended_by_admin && <span className="uw-tag">Recommended</span>}
                      </div>
                      <div className="uw-bids">
                        <div className="uw-bid-line">
                          <span className="uw-bid-label">Current bid</span>
                          <span className="uw-bid-value">&euro;{currentBid.toLocaleString('fr-FR')}</span>
                        </div>
                        <div className="uw-bid-line">
                          <span className="uw-bid-label">User bid</span>
                          <span className="uw-bid-value">{item.user_bid ? `\u20AC${Math.ceil(item.user_bid).toLocaleString('fr-FR')}` : '\u2014'}</span>
                        </div>
                      </div>
                      <div className="uw-notes">
                        <div className="uw-notes-label">Notes</div>
                        <div className="uw-notes-value">{item.notes || <span style={{ color: 'var(--color-text-muted)' }}>—</span>}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'bids' && (
        <div className="admin-section">
          <h2>All bids</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {bids.map(b => {
              let firstImage = null;
              try {
                const imgs = typeof b.images === 'string' ? JSON.parse(b.images) : b.images;
                firstImage = imgs?.[0];
              } catch {}
              return (
                <div key={b.id} style={{ display: 'flex', gap: 12, alignItems: 'center', background: 'white', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: 12, cursor: 'pointer' }}
                  onClick={() => window.open(`/products/${b.product_id}`, '_blank')}>
                  {firstImage ? (
                    <img src={firstImage} alt="" style={{ width: 56, height: 56, borderRadius: 4, objectFit: 'contain', background: '#f5f4f0', flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: 56, height: 56, borderRadius: 4, background: '#f5f4f0', flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)' }}>{b.brand}</div>
                    <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.clean_title}</div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{b.prenom} {b.nom} — {b.email}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>&euro;{b.amount_euro?.toLocaleString('fr-FR')}</div>
                    <div style={{ fontSize: 12, color: b.status === 'active' ? 'var(--color-success)' : b.status.includes('overbid') ? 'var(--color-error)' : 'var(--color-text-muted)', fontWeight: 500 }}>{b.status}</div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{new Date(b.created_at).toLocaleDateString('en-GB')}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === 'validation' && (
        <div className="admin-section">
          <h2>Model validation queue ({validationQueue.length})</h2>
          {validationQueue.length === 0 && <p className="empty">Queue is empty.</p>}
          {validationQueue.map(item => {
            let firstImg = null;
            try { const imgs = typeof item.images === 'string' ? JSON.parse(item.images) : item.images; firstImg = imgs?.[0]; } catch {}
            const vf = validationForm[item.id] || {};
            return (
              <div key={item.id} className="validation-row">
                <div className="validation-image">
                  {firstImg && <img src={firstImg} alt="" />}
                </div>
                <div className="validation-info">
                  <div className="admin-meta">{item.original_title}</div>
                  <div style={{ marginTop: 8, fontSize: 13 }}>
                    Suggested: <b>{item.suggested_brand}</b> \u00b7 {item.suggested_model} \u00b7 {item.suggested_model_number}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 12 }}>
                    {[['validated_brand', 'Brand', item.suggested_brand], ['validated_model', 'Model', item.suggested_model], ['validated_model_number', 'Model No.', item.suggested_model_number]].map(([key, label, placeholder]) => (
                      <div key={key}>
                        <label style={labelStyle}>{label}</label>
                        <input className="input" defaultValue={placeholder || ''} onChange={e => setValidationForm(f => ({ ...f, [item.id]: { ...f[item.id], [key]: e.target.value } }))} />
                      </div>
                    ))}
                  </div>
                  <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={() => validate.mutate({ id: item.id, data: { validated_brand: vf.validated_brand || item.suggested_brand || '', validated_model: vf.validated_model || item.suggested_model || '', validated_model_number: vf.validated_model_number || item.suggested_model_number || '' } })}>
                    Confirm
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === 'scraping' && <ScrapingTab />}

      {tab === 'notifications' && (
        <div className="admin-section">
          <h2>Send notification</h2>
          <div style={{ maxWidth: 500, display: 'grid', gap: 16 }}>
            <div>
              <label style={labelStyle}>Send to</label>
              <select className="input" value={notifData.user_id} onChange={e => setNotifData(d => ({ ...d, user_id: e.target.value }))}>
                <option value="">All approved users (broadcast)</option>
                {users.filter(u => u.statut === 'approved').map(u => (
                  <option key={u.id} value={u.id}>{u.prenom} {u.nom} ({u.email})</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Title</label>
              <input className="input" value={notifData.titre} onChange={e => setNotifData(d => ({ ...d, titre: e.target.value }))} />
            </div>
            <div>
              <label style={labelStyle}>Message</label>
              <textarea className="input" rows={4} value={notifData.message} onChange={e => setNotifData(d => ({ ...d, message: e.target.value }))} />
            </div>
            <button className="btn btn-primary" onClick={() => sendNotif.mutate()} disabled={!notifData.titre || !notifData.message}>
              Send {notifData.user_id ? 'to user' : 'to all users'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
